import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Subject } from 'rxjs';
import * as CookieConsent from 'vanilla-cookieconsent';
import { ConsentModalLayout, ConsentModalPosition, PreferencesModalPosition } from 'vanilla-cookieconsent';
import { environment } from '../../../environments/environment';

/**
 * Service used to handle cookie consent.
 * Cookie consent is based on CookieConsent v3 library: https://cookieconsent.orestbida.com/
 */
@Injectable({
    providedIn: 'root',
})
export class CookieConsentService {
    readonly translocoService = inject(TranslocoService);

    /**
     * Subject to notify a consent change
     */
    private consentChangeSubject = new Subject<string>();

    /**
     * Observable for consent change for other services
     */
    public consentChange$ = this.consentChangeSubject.asObservable();

    /**
     * Currently accepted categories
     */
    private acceptedCategories: string[] = [];

    /**
     * Initializes the cookie consent library with the correct configuration object based on the current language and runs it.
     */
    initCookieConsent() {
        const translations = this.translocoService.getTranslation('cookieConsent');

        let footerLinks = [];
        if (environment.cookie.cookieUrl) {
            footerLinks.push(
                `<a href="${environment.cookie.cookieUrl}" target="_blank">${translations['consentModal.footer.cookiePolicy']}</a>`
            );
        }

        let configuration = {
            mode: 'opt-in' as 'opt-in',
            autoShow: true,
            disablePageInteraction: true,
            guiOptions: {
                consentModal: {
                    layout: 'box wide' as ConsentModalLayout,
                    position: 'middle center' as ConsentModalPosition,
                    flipButtons: true,
                    equalWeightButtons: true,
                },
                preferencesModal: {
                    layout: 'box' as 'box',
                    position: 'left' as PreferencesModalPosition,
                    flipButtons: false,
                    equalWeightButtons: true,
                },
            },
            categories: {
                necessary: {
                    enabled: true, // this category is enabled by default
                    readOnly: true, // this category cannot be disabled
                },
                performance: {
                    enabled: true,
                    readOnly: false,
                },
            },
            language: {
                default: this.translocoService.getActiveLang(), //CookieConsent requires a default language, and its translations must be loaded here. To resolve this, simply set the active lang as default every time.
                translations: {
                    [this.translocoService.getActiveLang()]: {
                        consentModal: {
                            title: translations['consentModal.title'],
                            description: translations['consentModal.description'],
                            acceptAllBtn: translations['consentModal.acceptAllBtn'],
                            acceptNecessaryBtn: translations['consentModal.acceptNecessaryBtn'],
                            showPreferencesBtn: translations['consentModal.showPreferencesBtn'],
                            footer: footerLinks.join(' '),
                        },
                        preferencesModal: {
                            title: translations['preferencesModal.title'],
                            acceptAllBtn: translations['preferencesModal.acceptAllBtn'],
                            acceptNecessaryBtn: translations['preferencesModal.acceptNecessaryBtn'],
                            savePreferencesBtn: translations['preferencesModal.savePreferencesBtn'],
                            closeIconLabel: translations['preferencesModal.closeIconLabel'],
                            sections: translations['preferencesModal.sections'].map((section: any) => ({
                                title: section.title,
                                description: section.description,
                                linkedCategory: section.linkedCategory,
                            })),
                        },
                    },
                },
            },
            /**
             * This event is triggered the very first time the user expresses their choice of consent but also on every subsequent page load (not on consent change).
             */
            onConsent: ({ cookie }) => {
                this.acceptedCategories = cookie.categories;
            },
            /**
             * This event is triggered on consent change and only if consent has already been provided.
             */
            onChange: ({ cookie }) => {
                // Compare new categories to old
                const newAcceptedCategories = cookie.categories;
                const lostCategories = this.acceptedCategories.filter((cat) => !newAcceptedCategories.includes(cat));

                if (lostCategories.length > 0) {
                    lostCategories.forEach((cat) => {
                        //Notify consent loss
                        this.consentChangeSubject.next(cat);
                    });
                }

                //Update current accepted categories
                this.acceptedCategories = newAcceptedCategories;
            },
        };

        CookieConsent.reset();
        CookieConsent.run(configuration).then();
    }

    /**
     * Returns if a cookie category is accepted or not.
     * @param category
     */
    isCategoryAccepted(category: string): boolean {
        return CookieConsent.acceptedCategory(category);
    }
}
